import moment from 'moment-timezone'

/**
 * Helper functions for ScheduledTab component
 * These utilities are extracted to keep the main component clean and reusable
 */

/**
 * Transforms API event data into booking format expected by BookingListView component
 *
 * Why it's used:
 * - API returns events in one format, but BookingListView expects a different structure
 * - Handles timezone conversion for date/time display
 * - Provides fallback values for missing data
 * - Centralizes transformation logic for reusability
 *
 * @param {Object} event - Event object from API (getCoachScheduledList)
 * @param {string} timezone - User's selected timezone (e.g., 'America/New_York')
 * @param {string} selectedStatus - Status filter value (currently not used in API, but kept for display)
 * @returns {Object} Booking object with formatted date/time and all required fields
 */
export const transformEventToBooking = (event, timezone, selectedStatus) => {
  let dateStr = '--'
  let timeStr = '--'

  // Format date and time in user's selected timezone
  if (event.startDateTime) {
    try {
      const date = new Date(event.startDateTime)
      if (!isNaN(date.getTime()) && timezone) {
        // Use timezone-aware formatting when timezone is provided
        const dateOptions = {
          timeZone: timezone,
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }
        const timeOptions = {
          timeZone: timezone,
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        }
        dateStr = new Intl.DateTimeFormat('en-US', dateOptions).format(date)
        timeStr = new Intl.DateTimeFormat('en-US', timeOptions).format(date)
      } else if (!isNaN(date.getTime())) {
        // Fallback to local timezone if timezone not provided
        dateStr = date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
        timeStr = date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        })
      }
    } catch (e) {
      // Keep defaults on error
    }
  }

  const clientName = event.clientName || 'No client'
  const clientId = event.clientId || null

  // Helper function to get session label from condition
  const getSessionLabelFromCondition = (event) => {
    // Check if there's a sessionIndex in schedulingCondition
    const sessionIndex = event?.schedulingCondition?.sessionIndex
    if (sessionIndex) {
      // For quest sessions with conditional scheduling, show Pre-Session, Post-Session, etc.
      const sowType = event?.sowType

      if (sowType === 'QUEST') {
        // For QUEST sessions, show Pre-Session, Post-Session based on sessionIndex
        if (sessionIndex === 1) {
          return 'Preparation Session'
        } else if (sessionIndex === 2) {
          return 'Integration Session'
        }
        return `Session ${sessionIndex}`
      } else {
        // For coaching: show Session 1, Session 2, Session 3
        return `Session ${sessionIndex}`
      }
    }

    // If no condition or sessionIndex, return empty string
    return ''
  }

  // Return booking object matching BookingListView's expected format
  return {
    _id: event.eventId,
    id: event.eventId,
    clientName: clientName,
    clientId: clientId,
    // Coach of the booking. Both are empty when the coach account was deleted.
    coachId: event.coachId || null,
    coachName: event.coachName || '',
    eventTitle: event.summary || '-',
    eventName: event.summary || '-',
    sessionLabel: getSessionLabelFromCondition(event), // Add session label
    date: dateStr,
    scheduledDate: dateStr,
    time: timeStr,
    scheduledTime: timeStr,
    status: selectedStatus || 'scheduled',
    attendees: event.attendees || [],
    startDateTime: event.startDateTime,
    sowId: event.sowId || null,
    planName: event.planName || null,
    duration: event.duration || event.sowSessionDuration || null,
    durationLabel:
      event.durationLabel || event.sessionDurationLabel || event.duration || '',
    // Include conditional scheduling properties for reschedule flow
    isConditionalSchedulingEnabled:
      event.isConditionalSchedulingEnabled || false,
    schedulingCondition: event.schedulingCondition || null,
    // BE sends sowType as REGULAR/QUEST (or regular/quest); default to REGULAR
    sowType: (() => {
      const raw = (event.sowType || '').toString().toUpperCase()
      return raw === 'QUEST' ? 'QUEST' : 'REGULAR'
    })(),
    timezone: event.timezone || event.timeZone || null,
    // Used by Add log session modal (session-type options + calendar prefill)
    includeGrowthSession: !!event.includeGrowthSession,
    // Mongo id from BE (`scheduleEventId`) for /sows/:sowId/scheduled-events prefill
    scheduleEventId:
      event.scheduleEventId ||
      event.scheduledEventId ||
      event.scheduledEventObjectId ||
      null,
    // Include quest-related fields for description generation
    questTitle:
      event.questTitle || event.questName || event.quest?.title || null,
    questName: event.questName || event.quest?.name || null,
  }
}

/**
 * Generates description for booking without coach name
 * Similar to client platform but without coach name at the end
 *
 * @param {Object} booking - Booking object with quest and session information
 * @returns {string} Description string like "Quest Name - Preparation Session" or "Quest Name -  Integration Session"
 */
export const getDescriptionWithoutCoach = (booking) => {
  const sowType = booking?.sowType
  const questTitle = booking?.questName || ''

  if (sowType === 'QUEST') {
    // For scheduled sessions, use single sessionIndex from schedulingCondition
    if (booking?.schedulingCondition?.sessionIndex) {
      const sessionIndex = booking.schedulingCondition.sessionIndex
      let sessionLabel = ''
      if (sessionIndex === 1) {
        sessionLabel = 'Preparation Session'
      } else if (sessionIndex === 2) {
        sessionLabel = ' Integration Session'
      } else if (sessionIndex) {
        sessionLabel = `Session ${sessionIndex}`
      }

      return questTitle
        ? `${questTitle} - ${sessionLabel}`
        : sessionLabel || 'Experience Session'
    }

    // Fallback: try direct sessionIndex or use sessionLabel
    const sessionIndex = booking?.sessionIndex
    const sessionLabel = booking?.sessionLabel || ''
    let label = sessionLabel

    if (!label && sessionIndex) {
      if (sessionIndex === 1) {
        label = 'Preparation Session'
      } else if (sessionIndex === 2) {
        label = ' Integration Session'
      } else {
        label = `Session ${sessionIndex}`
      }
    }

    if (!label) {
      label = 'Pre and  Integration Session'
    }

    return questTitle ? `${questTitle} - ${label}` : label
  }

  // For non-QUEST sessions, return event title or coaching plan
  return booking?.eventTitle || booking?.eventName || 'Coaching Plan'
}

/**
 * Email validation regex pattern
 *
 * Why it's used:
 * - Validates guest email addresses when adding guests to rescheduled sessions
 * - Ensures only valid email formats are accepted
 * - Reusable across the scheduling module
 */
export const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

/**
 * Calculates UTC start and end times for rescheduling a session
 *
 * Why it's used:
 * - Handles complex timezone conversion when rescheduling sessions
 * - User selects date/time in their timezone, but API requires UTC
 * - Combines selected date with selected slot time in user's timezone
 * - Converts the combined datetime back to UTC for API submission
 * - Handles different duration scenarios (from booking duration or slot end time)
 *
 * Logic flow:
 * 1. Extract time components from slot (which is in UTC) in user's timezone
 * 2. Combine selected date with extracted time in user's timezone
 * 3. Convert combined datetime to UTC for API
 * 4. Calculate end time based on duration or slot end time
 *
 * @param {Object} rescheduleSelection - Contains date (ISO string) and slot (with start/end in UTC)
 * @param {Object} selectedBooking - Original booking with duration information
 * @param {string} timezone - User's selected timezone
 * @returns {Object|null} Object with slotStart and slotEnd in ISO format, or null if invalid
 */
export const calculateRescheduleTimes = (
  rescheduleSelection,
  selectedBooking,
  timezone,
) => {
  if (!timezone || !rescheduleSelection?.date || !rescheduleSelection?.slot) {
    return null
  }

  // Step 1: Get the time components from the slot (which is in UTC) in user's timezone
  // This gives us the hour/minute/second that the user sees
  const slotInUserTimezone = moment
    .utc(rescheduleSelection.slot.start)
    .tz(timezone)
  const timeOfDay = {
    hour: slotInUserTimezone.hour(),
    minute: slotInUserTimezone.minute(),
    second: slotInUserTimezone.second(),
  }

  // Step 2: Combine selected date with the time components in user's timezone
  const selectedDateInTimezone = moment.tz(rescheduleSelection.date, timezone)

  // Step 3: Create the start moment by combining date + time in user's timezone, then convert to UTC
  const slotStartMoment = moment
    .tz(
      [
        selectedDateInTimezone.year(),
        selectedDateInTimezone.month(),
        selectedDateInTimezone.date(),
        timeOfDay.hour,
        timeOfDay.minute,
        timeOfDay.second || 0,
      ],
      timezone,
    )
    .utc()

  // Step 4: Calculate end time based on duration or slot end time
  const eventDurationMinutes = Number(selectedBooking?.duration)
  let slotEndMoment
  if (eventDurationMinutes) {
    // Use booking duration if available
    slotEndMoment = slotStartMoment.clone().add(eventDurationMinutes, 'minutes')
  } else if (rescheduleSelection.slot?.end) {
    // Use slot end time if duration not available
    const endInUserTimezone = moment
      .utc(rescheduleSelection.slot.end)
      .tz(timezone)
    const endTimeOfDay = {
      hour: endInUserTimezone.hour(),
      minute: endInUserTimezone.minute(),
      second: endInUserTimezone.second(),
    }
    slotEndMoment = moment
      .tz(
        [
          selectedDateInTimezone.year(),
          selectedDateInTimezone.month(),
          selectedDateInTimezone.date(),
          endTimeOfDay.hour,
          endTimeOfDay.minute,
          endTimeOfDay.second || 0,
        ],
        timezone,
      )
      .utc()
  } else {
    // Fallback: same as start time
    slotEndMoment = slotStartMoment.clone()
  }

  return {
    slotStart: slotStartMoment.toISOString(),
    slotEnd: slotEndMoment.toISOString(),
  }
}

/**
 * Prepares and cleans the attendee list for rescheduling API call
 *
 * Why it's used:
 * - Combines primary attendee email (from form) with guest emails
 * - Removes empty/null values
 * - Removes duplicate emails (case-insensitive)
 * - Ensures clean data format for API submission
 *
 * @param {Object} rescheduleForm - Form object containing email field
 * @param {Array} guestList - Array of guest email strings
 * @returns {Array} Cleaned array of unique email addresses
 */
export const prepareRescheduleAttendees = (rescheduleForm, guestList) => {
  return [rescheduleForm.email, ...guestList]
    .filter((email) => !!email) // Remove empty/null/undefined emails
    .filter((email, index, self) => {
      // Remove duplicates (case-insensitive comparison)
      const lower = String(email).toLowerCase()
      return (
        self.findIndex((item) => String(item).toLowerCase() === lower) === index
      )
    })
}
